<template>
  <div class="product-reviews-page">
    <section class="intro-block">
      <p class="kicker">卫星数据质量 · 权威验证</p>
      <h1>
        整合多颗卫星、多类型数据产品的评价报告，包括辐射校正精度、几何定位精度、光谱一致性及整体数据质量等关键指标，
        为用户提供权威、客观、可视化的产品质量呈现。
      </h1>
      <p class="intro-description">
        评价报告均基于星地同步测试、标准化辐射/几何校正方法、参考数据对比分析等专业技术流程，可作为科研、工程应用、
        行业项目的质量参考。
      </p>
      <!-- <p class="intro-instruction">
        请选择筛选条件，点击左侧“搜索”按钮加载对应年度与卫星的验证报告；调整筛选条件后需重新搜索才能查看最新结果。
      </p> -->
    </section>

    <section class="evaluation-panel">
      <aside class="filter-panel">
        <div class="filter-group">
          <p class="filter-label">年份</p>
          <div class="chip-grid">
            <button
              v-for="year in years"
              :key="year"
              class="chip"
              :class="{ active: year === selectedYear }"
              @click="selectYear(year)"
            >
              {{ year }}
            </button>
          </div>
        </div>

        <div class="filter-group">
          <p class="filter-label">卫星类型</p>
          <div class="chip-grid">
            <button
              v-for="sat in satelliteTypes"
              :key="sat"
              class="chip"
              :class="{ active: sat === selectedSatellite }"
              @click="selectSatellite(sat)"
            >
              {{ sat }}
            </button>
          </div>
        </div>

      </aside>

      <div class="report-view">
        <div v-if="searchTriggered">
          <div v-if="currentReport" class="report-summary">
            <div>
              <p class="kicker small">
                {{ currentReport.year }} · {{ currentReport.satellite }}
              </p>
              <h2>{{ currentReport.title }}</h2>
              <!-- <p class="summary-text">
                {{ currentReport.summary || '该报告包含辐射、几何与光谱一致性等核心指标的测试结论，可作为项目评估参考。' }}
              </p> -->
            </div>
          </div>
          <div v-else class="report-empty">
            <p>暂无匹配的评价报告，请调整筛选条件。</p>
          </div>
        </div>
        <div v-else class="report-empty">
          <p>请选择年份和卫星类型以查看验证报告。</p>
        </div>

        <div class="pdf-container" v-if="searchTriggered && currentReport && currentReport.file">
          <iframe
            :src="currentReport.file"
            title="验证报告"
            frameborder="0"
            class="pdf-frame"
          ></iframe>
          <div class="pdf-actions">
            <a :href="currentReport.file" download class="download-link">下载报告 PDF</a>
            <router-link to="/download" class="download-link secondary">前往下载中心</router-link>
          </div>
        </div>
        <div class="pdf-placeholder" v-else>
          <p v-if="!searchTriggered">完成筛选即可加载并在线预览验证报告 PDF。</p>
          <p v-else-if="searchTriggered && currentReport && !currentReport.file" class="missing-tip">
            找不到 {{ currentReport.fileName }}，请确认文件已放入 `src/assets/卫星评价报告/` 目录。
          </p>
          <p v-else>暂无匹配的评价报告，请尝试其他年份或卫星类型。</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const reportContext = require.context('../assets/卫星评价报告', false, /\.pdf$/)
const reportList = reportContext
  .keys()
  .map((key) => {
    const fileName = key.replace('./', '')
    const match = fileName.match(/^(\d{4})年卫星数据产品验证报告-(.+)\.pdf$/i)
    if (!match) {
      return null
    }
    const year = Number(match[1])
    const satellite = match[2].toUpperCase()
    const friendlySatellite = satellite.replace(/\.\./g, '-')
    return {
      year,
      satellite: friendlySatellite,
      title: fileName,
      fileName,
    
      file: reportContext(key)
    }
  })
  .filter(Boolean)
  .sort((a, b) => b.year - a.year || a.satellite.localeCompare(b.satellite))

const uniqueYears = Array.from(new Set(reportList.map((item) => item.year))).sort((a, b) => b - a)
const uniqueSatellites = Array.from(new Set(reportList.map((item) => item.satellite)))

export default {
  name: 'ProductReviews',
  data() {
    return {
      reports: reportList,
      years: uniqueYears,
      satelliteTypes: uniqueSatellites,
      selectedYear: null,
      selectedSatellite: null,
      activeReportIndex: 0,
      searchTriggered: true
    }
  },
  computed: {
    filteredReports() {
      return this.reports.filter((report) => {
        const matchYear = this.selectedYear ? report.year === this.selectedYear : true
        const matchSatellite = this.selectedSatellite ? report.satellite === this.selectedSatellite : true
        return matchYear && matchSatellite
      })
    },
    currentReport() {
      return this.filteredReports[this.activeReportIndex] || null
    }
  },
  methods: {
    selectYear(year) {
      this.selectedYear = this.selectedYear === year ? null : year
      this.activeReportIndex = 0
      this.searchTriggered = true
    },
    selectSatellite(satellite) {
      this.selectedSatellite = this.selectedSatellite === satellite ? null : satellite
      this.activeReportIndex = 0
      this.searchTriggered = true
    }
  }
}
</script>

<style scoped>
.product-reviews-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  color: var(--text-primary);
}

.intro-block {
  border: 1px solid rgba(0, 180, 255, 0.3);
  border-radius: 18px;
  padding: 32px 36px;
  background: rgba(5, 5, 7, 0.65);
  line-height: 1.8;
}

.kicker {
  color: var(--secondary);
  letter-spacing: 0.2em;
  font-size: 0.9rem;
  text-transform: uppercase;
}

.kicker.small {
  font-size: 0.75rem;
  letter-spacing: 0.3em;
}

.intro-block h1 {
  font-size: 1.7rem;
  margin: 18px 0;
  font-weight: 600;
}

.intro-description {
  color: var(--text-secondary);
  max-width: 920px;
}

.intro-instruction {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-top: 12px;
}

.evaluation-panel {
  margin-top: 36px;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 28px;
}

.filter-panel {
  border: 1px solid rgba(0, 180, 255, 0.25);
  border-radius: 16px;
  padding: 24px;
  background: rgba(4, 4, 8, 0.85);
}

.filter-group + .filter-group {
  margin-top: 26px;
}

.filter-label {
  font-weight: 600;
  margin-bottom: 12px;
}

.chip-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

.chip {
  padding: 10px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
}

.chip.active {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  color: #050505;
  border-color: transparent;
  box-shadow: 0 12px 20px rgba(0, 180, 255, 0.25);
}

.chip:not(.active):hover {
  border-color: var(--primary);
  color: var(--primary);
}

.search-btn {
  width: 100%;
  margin-top: 30px;
  padding: 12px;
  border-radius: 999px;
  border: none;
  background: var(--primary);
  color: #050505;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.search-btn:hover {
  transform: translateY(-2px);
}

.report-view {
  border: 1px solid rgba(0, 180, 255, 0.25);
  border-radius: 18px;
  padding: 28px;
  background: rgba(8, 8, 12, 0.92);
  min-height: 620px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.report-summary {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.report-summary h2 {
  margin: 8px 0;
}

.summary-text {
  color: var(--text-secondary);
  max-width: 720px;
}

.report-empty,
.pdf-placeholder {
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  color: var(--text-secondary);
}

.missing-tip {
  margin-top: 12px;
  font-size: 0.9rem;
  color: #ffdc8b;
}

.pdf-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pdf-frame {
  width: 100%;
  height: 560px;
  border-radius: 16px;
  background: #fff;
}

.pdf-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.download-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
}

.download-link.secondary {
  color: var(--secondary);
}

@media (max-width: 992px) {
  .evaluation-panel {
    grid-template-columns: 1fr;
  }

  .filter-panel {
    display: flex;
    flex-direction: column;
  }
}

@media (max-width: 640px) {
  .chip-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pdf-frame {
    height: 380px;
  }
}
</style>

